import React, { useState } from 'react';
import { Link as RouterLink, useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar, Toolbar, Box, Button, IconButton, Menu, MenuItem,
  Container, Stack, useScrollTrigger, Typography,
  Drawer, List, ListItemButton, ListItemText, ListSubheader, Divider,
} from '@mui/material';
import LanguageIcon from '@mui/icons-material/Language';
import MenuIcon from '@mui/icons-material/Menu';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { useTranslation } from 'react-i18next';
import { authService } from '../pages/services/api';
import { tokens } from '../theme';

const navLinks = [
  { to: '/pricing',  labelKey: 'nav.pricing' },
  { to: '/api-docs', labelKey: 'nav.apiDocs' },
  { to: '/status',   labelKey: 'nav.status' },
];

// Below md (900 px) the inline links are hidden; the burger panel carries every public section.
const mobileLinks = [
  { to: '/',               labelKey: 'nav.home' },
  ...navLinks,
  { to: '/deliverability', labelKey: 'nav.deliverability' },
];

const toolLinks = [
  { to: '/tools/email-validate',   labelKey: 'nav.tools.emailValidate' },
  { to: '/tools/spf-check',        labelKey: 'nav.tools.spf' },
  { to: '/tools/dmarc-check',      labelKey: 'nav.tools.dmarc' },
  { to: '/tools/blacklist-check',  labelKey: 'nav.tools.blacklist' },
];

// At 375 px the logo, language icon and both auth buttons only fit with tighter buttons.
const compactOnXs = { px: { xs: 1, md: 2 }, minWidth: { xs: 0, md: 64 }, fontSize: { xs: '0.8125rem', md: undefined } };

interface NavBarProps {
  userName?: string;
  userPlan?: string;
}

export const NavBar: React.FC<NavBarProps> = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const trigger = useScrollTrigger({ disableHysteresis: true, threshold: 8 });

  const [toolsAnchor, setToolsAnchor] = useState<null | HTMLElement>(null);
  const [langAnchor, setLangAnchor]   = useState<null | HTMLElement>(null);
  const [mobileOpen, setMobileOpen]   = useState(false);

  const token = localStorage.getItem('auth_token');

  const handleLanguageToggle = async () => {
    const newLang = i18n.language.startsWith('ru') ? 'en' : 'ru';
    i18n.changeLanguage(newLang);
    if (token) {
      try { await authService.updateLocale(newLang); } catch {}
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('auth_token');
    navigate('/');
  };

  return (
    <AppBar position="sticky" sx={{
      backgroundColor: trigger ? 'rgba(10,10,15,0.8)' : tokens.color.bg,
      transition: 'background-color 0.2s',
    }}>
      <Container maxWidth="lg">
        <Toolbar disableGutters sx={{ minHeight: 56, gap: { xs: 1, md: 3 } }}>
          <IconButton
            aria-label="menu"
            edge="start"
            onClick={() => setMobileOpen(true)}
            sx={{ display: { xs: 'inline-flex', md: 'none' }, color: tokens.color.textSecondary }}
          >
            <MenuIcon />
          </IconButton>
          <RouterLink to="/" style={{ textDecoration: 'none' }}>
            <Typography variant="h4" sx={{ fontWeight: 700, color: tokens.color.textPrimary, fontSize: { xs: '1.125rem', md: undefined } }}>
              Email<Box component="span" sx={{ color: tokens.color.accent }}>Validator</Box>
            </Typography>
          </RouterLink>

          <Stack direction="row" spacing={1} sx={{ flexGrow: 1, ml: 2, display: { xs: 'none', md: 'flex' } }}>
            {navLinks.map(l => (
              <Button
                key={l.to}
                component={RouterLink}
                to={l.to}
                sx={{
                  color: location.pathname === l.to ? tokens.color.textPrimary : tokens.color.textSecondary,
                  '&:hover': { color: tokens.color.textPrimary, backgroundColor: 'transparent' },
                }}
              >
                {t(l.labelKey)}
              </Button>
            ))}
            <Button
              endIcon={<KeyboardArrowDownIcon />}
              onClick={e => setToolsAnchor(e.currentTarget)}
              sx={{
                color: location.pathname.startsWith('/tools') ? tokens.color.textPrimary : tokens.color.textSecondary,
                '&:hover': { color: tokens.color.textPrimary, backgroundColor: 'transparent' },
              }}
            >
              {t('nav.tools.label')}
            </Button>
            <Menu
              anchorEl={toolsAnchor}
              open={Boolean(toolsAnchor)}
              onClose={() => setToolsAnchor(null)}
              PaperProps={{ sx: { mt: 1, minWidth: 200 } }}
            >
              {toolLinks.map(tl => (
                <MenuItem
                  key={tl.to}
                  component={RouterLink}
                  to={tl.to}
                  onClick={() => setToolsAnchor(null)}
                >
                  {t(tl.labelKey)}
                </MenuItem>
              ))}
            </Menu>
          </Stack>

          <Box sx={{ flexGrow: { xs: 1, md: 0 } }} />

          <IconButton onClick={e => setLangAnchor(e.currentTarget)} sx={{ display: { xs: 'none', md: 'inline-flex' }, color: tokens.color.textSecondary }}>
            <LanguageIcon fontSize="small" />
          </IconButton>
          <Menu
            anchorEl={langAnchor}
            open={Boolean(langAnchor)}
            onClose={() => setLangAnchor(null)}
          >
            <MenuItem onClick={() => { handleLanguageToggle(); setLangAnchor(null); }}>
              {i18n.language.startsWith('ru') ? 'English' : 'Русский'}
            </MenuItem>
          </Menu>

          {token ? (
            <Stack direction="row" spacing={1}>
              <Button component={RouterLink} to="/dashboard" variant="contained">
                {t('nav.dashboard')}
              </Button>
              <Button onClick={handleLogout} variant="outlined">
                {t('nav.logout')}
              </Button>
            </Stack>
          ) : (
            <Stack direction="row" spacing={{ xs: 0.5, md: 1 }} sx={{ flexShrink: 0 }}>
              <Button component={RouterLink} to="/login" sx={{ ...compactOnXs, color: tokens.color.textSecondary }}>
                {t('nav.login')}
              </Button>
              <Button component={RouterLink} to="/register" variant="contained" sx={compactOnXs}>
                {t('nav.register')}
              </Button>
            </Stack>
          )}
        </Toolbar>
      </Container>
      <Drawer
        anchor="left"
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        sx={{ display: { md: 'none' } }}
        PaperProps={{ 'data-testid': 'mobile-nav', sx: { width: 260, backgroundColor: tokens.color.surface } } as object}
      >
        <List component="nav">
          {mobileLinks.map(l => (
            <ListItemButton key={l.to} component={RouterLink} to={l.to} selected={location.pathname === l.to}
              onClick={() => setMobileOpen(false)}>
              <ListItemText primary={t(l.labelKey)} />
            </ListItemButton>
          ))}
          <Divider />
          <ListSubheader sx={{ backgroundColor: 'transparent' }}>{t('nav.tools.label')}</ListSubheader>
          {toolLinks.map(tl => (
            <ListItemButton key={tl.to} component={RouterLink} to={tl.to} selected={location.pathname === tl.to}
              onClick={() => setMobileOpen(false)}>
              <ListItemText primary={t(tl.labelKey)} />
            </ListItemButton>
          ))}
          <Divider />
          <ListItemButton onClick={() => { handleLanguageToggle(); setMobileOpen(false); }}>
            <LanguageIcon fontSize="small" sx={{ mr: 1.5, color: tokens.color.textSecondary }} />
            <ListItemText primary={i18n.language.startsWith('ru') ? 'English' : 'Русский'} />
          </ListItemButton>
        </List>
      </Drawer>
    </AppBar>
  );
};
